import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster, toast } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import AuthCallback from "./components/AuthCallback";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import PublicHomePage from "./pages/PublicHomePage";
import AboutPage from "./pages/AboutPage";
import RefinePage from "./pages/RefinePage";
import ConvertPage from "./pages/ConvertPage";
import CreateResumePage from "./pages/CreateResumePage";
import DownloadPage from "./pages/DownloadPage";
import MyResumesPage from "./pages/MyResumesPage";
import ProfilePage from "./pages/ProfilePage";
import UpgradePage from "./pages/UpgradePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import ResumeTemplateLanding from "./pages/ResumeTemplateLanding";
import MatchScorePage from "./pages/MatchScorePage";
import "./index.css";

// Check if localStorage/sessionStorage are available (non-blocking)
function checkStorageAvailable() {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id
  // This MUST be done during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicHomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/resume-template/:roleSlug" element={<ResumeTemplateLanding />} />
      
      {/* Google OAuth Callback - Direct integration */}
      <Route path="/auth/callback" element={<GoogleAuthCallback />} />
      
      {/* Semi-Public Routes - No login required until save/download */}
      <Route path="/convert" element={<ConvertPage />} />
      <Route path="/refine" element={<RefinePage />} />
      <Route path="/create-resume" element={<CreateResumePage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/match-score" element={<MatchScorePage />} />
      
      {/* Protected Routes - Login required */}
      <Route 
        path="/home" 
        element={<Navigate to="/" replace />}
      />
      <Route 
        path="/my-resumes" 
        element={
          <ProtectedRoute>
            <MyResumesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upgrade" 
        element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Non-blocking storage check - just warn, don't block
  useEffect(() => {
    const isAvailable = checkStorageAvailable();
    if (!isAvailable) {
      // Show a non-blocking warning toast instead of blocking modal
      setTimeout(() => {
        toast.warning(
          'Browser storage may be restricted. If you have issues logging in, please enable cookies in your browser settings.',
          { duration: 8000 }
        );
      }, 1000);
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
